import React, { useCallback, useContext, useEffect } from 'react';

import { useImmer } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';

import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { reportException } from '_lib/utils/sentryService';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import {
  BaseCompanyZoneZipSelect,
  createZoneZipSelectState,
  ZoneZipSelectState
} from '../../../../../common/CompanyPane/CompanySelected/BaseCompanyZipZoneSelect';
import { ZipZoneSelectComponentProps } from '../../../../../common/CompanyPane/CompanySelected/zipZoneSelectContext';
import {
  setHeldAppointmentContext,
  selectAppointmentBookingPaneOpen,
  selectHeldAppointmentContext,
  selectPhoneCallContext
} from '../../../StApiPhoneCall.slice';

export const CompanyZoneZipSelect = (props: ZipZoneSelectComponentProps): React.ReactElement => {
  const { onCompanySwitchConfirmed, companyContext, zoneZipContext } = props;

  const api = useContext(ApiContext);

  const dispatch = useAppDispatch();

  const appointmentBookingPaneOpen = useAppSelector(selectAppointmentBookingPaneOpen);

  const heldAppointmentContext = useAppSelector(selectHeldAppointmentContext);
  const phoneCallContext = useAppSelector(selectPhoneCallContext);

  const { enqueueSnackbar } = useOurSnackbar();

  const [zoneZipSelectState, setZoneZipSelectState] = useImmer<ZoneZipSelectState>(createZoneZipSelectState());

  const handleOverrideZipOrPostalNO = useCallback(() => {
    if (heldAppointmentContext.heldAppointment) {
      // clear out the appointment regardless if hardDelete succeeds (API cleanup task will take care of it if need be)
      dispatch(setHeldAppointmentContext({ heldAppointment: undefined }));
      enqueueSnackbar('Zip code changed. Appointment deleted.');
      api.appointments.hardDelete(heldAppointmentContext.heldAppointment.appointment.id).catch((err) =>
        reportException(err, {
          extra: {
            message: 'Appointment hard delete failed when releasing appointment on zip code change.',
            heldAppointment: heldAppointmentContext.heldAppointment
          }
        })
      );
    }
  }, [api.appointments, heldAppointmentContext, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (appointmentBookingPaneOpen) {
      setZoneZipSelectState((state) => {
        state.disabledText = 'Selection is disabled when an appointment is being scheduled.';
        state.disabled = true;
      });
    } else {
      setZoneZipSelectState((state) => {
        state.disabledText = '';
        state.disabled = false;
      });
    }
  }, [appointmentBookingPaneOpen, setZoneZipSelectState]);

  return (
    <BaseCompanyZoneZipSelect
      onCompanySwitchConfirmed={onCompanySwitchConfirmed}
      companyContext={companyContext}
      zoneZipContext={zoneZipContext}
      externalZipOrPostal={phoneCallContext.location?.zipOrPostal}
      zoneZipSelectState={zoneZipSelectState}
      setZoneZipSelectState={setZoneZipSelectState}
      onOverrideZipOrPostalNO={handleOverrideZipOrPostalNO}
    />
  );
};
